import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { SkeletonRows } from '../../components/skeleton';
import PendingIntakeDrawer from '../../components/modals/pendingIntakeDrawer';
import { approveClientIntake, deleteClientIntake, getClientIntakes, rejectClientIntake } from '../../services/clientIntakes';
import { onMutateSuccess } from '../../utils/queryResponse';
import { toLocalDateString } from '../../utils/helper';
import ConfirmationModal from '../../components/modals/confirmation';

const styles = `
  .sbr .pc-list{display:flex;flex-direction:column;gap:12px}
  .sbr .pc-card{background:#fff;border:1px solid var(--line);border-radius:var(--r-md,12px);box-shadow:var(--sh-1);padding:16px}
  .sbr .pc-top{display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap}
  .sbr .pc-nm{font-size:15px;font-weight:800;color:var(--ink)}
  .sbr .pc-when{font-size:12px;color:var(--muted);font-weight:600;margin-left:auto}
  .sbr .pc-src{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:800;letter-spacing:.03em;color:var(--muted);background:var(--surface,#f8fafc);border:1px solid var(--line);border-radius:999px;padding:3px 9px}
  .sbr .pc-src.link{color:var(--brand-ink,#1d4ed8);border-color:transparent;background:var(--brand-bg,#eff6ff)}
  .sbr .pc-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:8px 18px;margin-top:10px}
  .sbr .pc-grid .l{font-size:10.5px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--faint)}
  .sbr .pc-grid .v{font-size:13.5px;font-weight:600;color:var(--text);overflow-wrap:anywhere}
  .sbr .pc-dups{margin-top:12px;padding:10px 12px;border-radius:10px;background:var(--warn-bg);border:1px solid var(--warn-bd)}
  .sbr .pc-dups .t{font-size:12px;font-weight:800;color:var(--warn);margin-bottom:6px}
  .sbr .pc-dups label{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--ink);padding:4px 0;cursor:pointer}
  .sbr .pc-dups .by{font-size:11px;color:var(--muted);font-weight:700}
  .sbr .pc-acts{display:flex;gap:8px;flex-wrap:wrap;margin-top:14px}
  .sbr .pc-acts .danger{color:var(--bad)}
  .sbr .pc-filter{display:flex;gap:6px;margin-bottom:12px;flex-wrap:wrap}
  .sbr .pc-filter button{border:1px solid var(--line);background:#fff;color:var(--muted);border-radius:999px;padding:5px 12px;font:800 12px inherit;cursor:pointer}
  .sbr .pc-filter button.on{background:var(--ink,#0f172a);border-color:transparent;color:#fff}
  .sbr .pc-rev{font-size:12px;color:var(--muted);font-weight:600;margin-top:10px}
  .sbr .pc-st{font-size:10.5px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;padding:2px 8px;border-radius:999px;border:1px solid transparent}
  .sbr .pc-st.rejected{color:#b91c1c;background:#fee2e2}
  .sbr .pc-st.approved{color:#15803d;background:#dcfce7}
`;

const TABS_CONFIG = [
    { key: 'PENDING', labelKey: 'clients.pendingTab' },
    { key: 'REJECTED', labelKey: 'clients.rejected' },
    { key: 'APPROVED', labelKey: 'clients.approved' },
];

/**
 * Customer submissions from the public sign-up link, waiting for review.
 * Approve creates the client (or merges into an existing one the reviewer
 * picks from the detected duplicates); Reject keeps it out of the book.
 */
const PendingClients = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Which review state is on screen. Rejected and approved submissions are
    // kept in the database, so they stay reachable instead of appearing to
    // vanish the moment someone acts on them.
    const [status, setStatus] = useState('PENDING');

    // Submissions arrive from outside the app, so this list must not inherit
    // the global 24-hour staleTime — the office would never see a new request
    // without a hard reload. Refetch on mount and whenever the tab regains
    // focus, which is exactly when someone comes back to check.
    const { data, isLoading } = useQuery(['client-intakes', status], () => getClientIntakes(status), {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
    });
    const intakes = data?.code === 200 && Array.isArray(data.message) ? data.message : [];

    // Per-intake choice: '' = create a new client, otherwise the client id to merge into.
    const [mergeChoice, setMergeChoice] = useState({});
    // Id of the submission open in the detail drawer.
    const [detailId, setDetailId] = useState(null);
    // Deleting a customer request is permanent, so it is confirmed with the
    // app's own modal rather than a browser dialog.
    const [idToDelete, setIdToDelete] = useState(null);

    const refresh = () => {
        setDetailId(null);
        queryClient.invalidateQueries(['client-intakes']);
        queryClient.invalidateQueries(['clients']);
    };

    const { mutate: approve, isLoading: approving } = useMutation(approveClientIntake, {
        onSuccess: onMutateSuccess((client) => {
            refresh();
            if (client?.id) navigate(`/clients/${client.id}`);
        }, t('clients.clientSavedToast')),
    });
    const { mutate: reject, isLoading: rejecting } = useMutation(rejectClientIntake, {
        onSuccess: onMutateSuccess(refresh, t('clients.requestRejectedToast')),
    });
    const { mutate: remove } = useMutation(deleteClientIntake, {
        onSuccess: onMutateSuccess(refresh, t('clients.requestDeletedToast')),
    });

    const busy = approving || rejecting;

    // Resolved from the live list, so the drawer follows a refetch instead of
    // holding a stale copy of the submission.
    const detail = detailId ? intakes.find(i => i.id === detailId) : null;
    const detailChoice = detail ? (mergeChoice[detail.id] ?? '') : '';

    const filterBar = (
        <div className="pc-filter">
            {TABS_CONFIG.map((tb) => (
                <button key={tb.key} type="button" className={status === tb.key ? 'on' : ''} onClick={() => setStatus(tb.key)}>{t(tb.labelKey)}</button>
            ))}
        </div>
    );

    const EMPTY = {
        PENDING: [t('clients.noPendingRequests'), t('clients.pendingRequestsHint')],
        REJECTED: [t('clients.noRejectedRequests'), t('clients.rejectedRequestsHint')],
        APPROVED: [t('clients.noApprovedRequests'), t('clients.approvedRequestsHint')],
    }[status];

    if (isLoading) {
        return (
            <div className="pc-list">
                <style>{styles}</style>
                {filterBar}
                <div className="card-sbr"><SkeletonRows rows={4} widths={['60%', '40%']} /></div>
            </div>
        );
    }

    if (!intakes.length) {
        return (
            <div className="pc-list">
                <style>{styles}</style>
                {filterBar}
                <div className="card-sbr" style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)' }}>{EMPTY[0]}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--faint)', marginTop: 4 }}>{EMPTY[1]}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="pc-list">
            <style>{styles}</style>
            {filterBar}
            {intakes.map((it) => {
                const dups = Array.isArray(it.possibleDuplicates) ? it.possibleDuplicates : [];
                const choice = mergeChoice[it.id] ?? '';
                const name = it.clientType === 'COMPANY' && it.companyName ? `${it.companyName} (${it.firstName} ${it.lastName})` : `${it.firstName} ${it.lastName}`;
                const address = [it.street, it.city, it.state, it.zipCode, it.country].filter(Boolean).join(', ');
                const extra = [it.apartmentNo && `${t('clients.apartment')} ${it.apartmentNo}`, it.floor && `${t('clients.floor')} ${it.floor}`, it.intercomName && `${t('clients.intercom')} ${it.intercomName}`].filter(Boolean).join(' · ');
                // Where it came from: the company's public form, or a private
                // single-use link the office created for one enquiry.
                const viaLink = !!(it.intakeLinkId || it.intakeLink);
                const source = viaLink ? `${t('clients.privateLink')}${it.intakeLink?.label ? ` · ${it.intakeLink.label}` : ''}` : t('clients.publicForm');
                return (
                    <div key={it.id} className="pc-card">
                        <div className="pc-top">
                            <div className="pc-nm">{name}</div>
                            <span className={`pc-src${viaLink ? ' link' : ''}`}>{source}</span>
                            {it.status !== 'PENDING' && (
                                <span className={`pc-st ${it.status === 'APPROVED' ? 'approved' : 'rejected'}`}>{it.status === 'APPROVED' ? t('clients.approved') : t('clients.rejected')}</span>
                            )}
                            <span className="pc-when">{t('clients.submittedDate', { date: toLocalDateString(it.createdAt, 'dd.MM.yyyy HH:mm') })}</span>
                        </div>
                        <div className="pc-grid">
                            <div><div className="l">{t('clients.phone')}</div><div className="v"><a href={`tel:${it.phone}`}>{it.phone}</a></div></div>
                            <div><div className="l">{t('clients.email')}</div><div className="v">{it.email || '—'}</div></div>
                            <div style={{ gridColumn: '1 / -1' }}><div className="l">{t('clients.address')}</div><div className="v">{address}{extra && <span style={{ color: 'var(--muted)' }}> · {extra}</span>}</div></div>
                            {it.notes && <div style={{ gridColumn: '1 / -1' }}><div className="l">{t('clients.notes')}</div><div className="v">{it.notes}</div></div>}
                        </div>

                        {dups.length > 0 && it.status !== 'APPROVED' && (
                            <div className="pc-dups">
                                <div className="t">{t('clients.looksLikeExisting')}</div>
                                <label>
                                    <input type="radio" name={`m-${it.id}`} checked={choice === ''} onChange={() => setMergeChoice(m => ({ ...m, [it.id]: '' }))} />
                                    {t('clients.newClientRadio')}
                                </label>
                                {dups.map(d => (
                                    <label key={d.id}>
                                        <input type="radio" name={`m-${it.id}`} checked={choice === d.id} onChange={() => setMergeChoice(m => ({ ...m, [it.id]: d.id }))} />
                                        <Trans i18nKey="pendingIntake.updateExisting" values={{ name: d.name, phone: d.phone }} components={{ b: <b /> }} />
                                        {' '}<span className="by">· {d.matchedBy === 'phone' ? t('pendingIntake.samePhone') : d.matchedBy === 'email' ? t('pendingIntake.sameEmail') : t('pendingIntake.sameName')}</span>
                                    </label>
                                ))}
                            </div>
                        )}

                        {it.status !== 'PENDING' && (
                            <div className="pc-rev">
                                {it.status === 'APPROVED' ? t('clients.approvedByOn') : t('clients.rejectedByOn')}
                                {it.reviewedBy?.fullName ? ` ${t('clients.byName', { name: it.reviewedBy.fullName })}` : ''}
                                {it.reviewedAt ? ` ${t('clients.onDate', { date: toLocalDateString(it.reviewedAt, 'dd.MM.yyyy HH:mm') })}` : ''}
                            </div>
                        )}

                        <div className="pc-acts">
                            {it.status === 'APPROVED' ? (
                                it.clientId && (
                                    <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => navigate(`/clients/${it.clientId}`)}>
                                        {t('clients.openClient')}
                                    </button>
                                )
                            ) : (
                                <button type="button" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={busy}
                                    onClick={() => approve({ id: it.id, mergeIntoClientId: choice || undefined })}>
                                    {/* A rejected request can still be approved — that is how a
                                        rejection made by mistake is undone. */}
                                    {it.status === 'REJECTED' ? t('clients.approveAfterAll') : choice ? t('clients.approveAndUpdate') : t('clients.approveAsNew')}
                                </button>
                            )}
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={() => setDetailId(it.id)}>{t('clients.details')}</button>
                            {it.status === 'PENDING' && (
                                <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" disabled={busy} onClick={() => reject(it.id)}>{t('clients.reject')}</button>
                            )}
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr danger" disabled={busy}
                                onClick={() => setIdToDelete(it.id)}>{t('common.delete')}</button>
                        </div>
                    </div>
                );
            })}

            <PendingIntakeDrawer
                show={!!detailId}
                onHide={() => setDetailId(null)}
                intake={detail}
                choice={detailChoice}
                onChoice={(v) => detail && setMergeChoice(m => ({ ...m, [detail.id]: v }))}
                onApprove={() => detail && approve({ id: detail.id, mergeIntoClientId: detailChoice || undefined })}
                onReject={() => detail && reject(detail.id)}
                onDelete={() => detail && setIdToDelete(detail.id)}
                busy={busy}
            />

            <ConfirmationModal display={!!idToDelete} toggle={() => setIdToDelete(null)}
                title={t('clients.deleteRequestTitle')} confirmText={t('common.delete')} cancelText={t('clients.keepIt')}
                message={t('clients.deleteRequestConfirm')}
                confirmAction={() => { remove(idToDelete); setDetailId(null); }} />
        </div>
    );
};

export default PendingClients;
